import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { GripVertical, X } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function RoutePlanner() {
  const { t } = useLang();
  const [inspectors, setInspectors] = useState([]);
  const [selectedInsp, setSelectedInsp] = useState("");
  const [selectedDay, setSelectedDay] = useState(0);
  const [availableLooms, setAvailableLooms] = useState([]);
  const [route, setRoute] = useState([]); // ordered loom ids
  const [msg, setMsg] = useState("");
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => { api.get("/users/inspectors").then((r) => setInspectors(r.data)); }, []);
  useEffect(() => {
    if (!selectedInsp) return;
    api.get("/looms", { params: { inspector_id: selectedInsp } }).then((r) => setAvailableLooms(r.data));
    api.get("/routes", { params: { inspector_id: selectedInsp } }).then((r) => {
      const existing = r.data.find((x) => x.day_of_week === selectedDay);
      setRoute(existing?.loom_ids || []);
    });
  }, [selectedInsp, selectedDay]);

  const inRoute = availableLooms.filter((l) => route.includes(l.id)).sort((a, b) => route.indexOf(a.id) - route.indexOf(b.id));
  const notInRoute = availableLooms.filter((l) => !route.includes(l.id));

  const onDragStart = (id) => (e) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (err) { console.debug("[RoutePlanner] setData not supported:", err?.message); }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const dropOnRoute = (targetId) => (e) => {
    e.preventDefault();
    const src = draggingId || e.dataTransfer.getData("text/plain");
    if (!src) return;
    const without = route.filter((r) => r !== src);
    const targetIdx = targetId ? without.indexOf(targetId) : without.length;
    const insertAt = targetIdx >= 0 ? targetIdx : without.length;
    without.splice(insertAt, 0, src);
    setRoute(without);
    setDraggingId(null);
  };

  const dropOnPool = (e) => {
    e.preventDefault();
    const src = draggingId || e.dataTransfer.getData("text/plain");
    if (!src) return;
    setRoute(route.filter((r) => r !== src));
    setDraggingId(null);
  };

  const save = async () => {
    await api.put("/routes", { inspector_id: selectedInsp, day_of_week: selectedDay, loom_ids: route });
    setMsg("Saved");
    setTimeout(() => setMsg(""), 2000);
  };

  return (
    <div>
      <div className="eyebrow">{t("nav_route_planner")}</div>
      <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>Route Planner</h1>
      <p className="text-sm text-[#4A627A] mt-1">Drag looms from Available into the day's Route; reorder within the route to set visit sequence.</p>
      <div className="flex gap-2 flex-wrap mt-3">
        <select data-testid="planner-insp" value={selectedInsp} onChange={(e) => setSelectedInsp(e.target.value)} className="h-10 border rounded-md px-2 text-sm">
          <option value="">Select inspector</option>
          {inspectors.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.active_loom_count} looms)</option>)}
        </select>
        <div className="flex gap-1 flex-wrap">
          {DAYS.map((d, idx) => (
            <button
              key={d}
              data-testid={`day-${idx}`}
              onClick={() => setSelectedDay(idx)}
              className={`tap-target px-3 rounded-md border text-sm ${selectedDay === idx ? "bg-[#1B3A5C] text-white" : "bg-white border-[#1B3A5C]/15"}`}
            >{d}</button>
          ))}
        </div>
      </div>

      {selectedInsp && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div
              data-testid="pool-column"
              onDragOver={onDragOver}
              onDrop={dropOnPool}
              className="hag-surface p-3 min-h-[300px]"
            >
              <div className="eyebrow mb-2">Available looms</div>
              {notInRoute.map((l) => (
                <LoomCard key={l.id} loom={l} onDragStart={onDragStart(l.id)} testId={`avail-${l.loom_number}`} />
              ))}
              {notInRoute.length === 0 && <div className="text-xs text-[#7C8E9E]">All looms are in the route.</div>}
            </div>

            <div
              data-testid="route-column"
              onDragOver={onDragOver}
              onDrop={dropOnRoute(null)}
              className="hag-surface p-3 min-h-[300px]"
              style={{ borderColor: "rgba(27,58,92,0.25)" }}
            >
              <div className="eyebrow mb-2">{DAYS[selectedDay]} route ({inRoute.length})</div>
              {inRoute.map((l, idx) => (
                <div key={l.id} onDragOver={onDragOver} onDrop={dropOnRoute(l.id)}>
                  <LoomCard loom={l} idx={idx + 1} onDragStart={onDragStart(l.id)} testId={`route-${l.loom_number}`}
                    onRemove={() => setRoute(route.filter((r) => r !== l.id))} />
                </div>
              ))}
              {inRoute.length === 0 && <div className="text-xs text-[#7C8E9E]">Drop looms here to add to the route.</div>}
            </div>
          </div>
          <button data-testid="save-route-btn" onClick={save} className="mt-4 h-10 px-4 rounded-md font-semibold text-white" style={{ background: "var(--brand-navy)" }}>
            {t("save")}
          </button>
          {msg && <span className="ml-2 text-sm text-emerald-700">{msg}</span>}
        </>
      )}
    </div>
  );
}

function LoomCard({ loom, idx, onDragStart, testId, onRemove }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      data-testid={testId}
      className="border border-[#1B3A5C]/10 rounded-md p-2 mb-2 bg-white flex items-center gap-2 cursor-grab active:cursor-grabbing"
    >
      <GripVertical size={14} className="text-[#7C8E9E]" />
      {idx && <span className="text-xs font-bold text-[#4A627A] w-5">{idx}</span>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{loom.loom_number}</div>
        <div className="text-xs text-[#7C8E9E]">{loom.weaver_name} · Zone {loom.zone || "?"}</div>
      </div>
      {onRemove && (
        <button onClick={onRemove} className="text-[#7C8E9E] hover:text-red-600" data-testid={`remove-${loom.loom_number}`}><X size={14} /></button>
      )}
    </div>
  );
}
